import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { eggState, announceEgg, playBells, isMidnightPreview } from '../../lib/eggs';

// A brass-rimmed wall clock (numbers 1–12) showing the time in the gallery's world.
// It starts at the visitor's real local time. If they switch to another time of day, the clock
// jumps to a fitting time for it (and keeps ticking from there); switching back to their own time
// of day shows the real time again.
// Secret: when it strikes midnight it rings twelve soft bells and the picture lights pulse.
const SHOWN_TIME = { morning: [8, 30], afternoon: [14, 30], evening: [19, 0], night: [23, 15] };

// Offset (ms) from the real clock that makes it show the given time of day.
export const clockOffsetFor = (timeOfDay, localTimeOfDay, now = new Date()) => {
  if (!timeOfDay || timeOfDay === localTimeOfDay) return 0;
  const [h, m] = SHOWN_TIME[timeOfDay] || [12, 0];
  const target = new Date(now);
  target.setHours(h, m, now.getSeconds(), now.getMilliseconds());
  return target - now;
};

const makeFaceTexture = () => {
  const s = 512;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const ctx = c.getContext('2d');
  const r = s / 2;
  const g = ctx.createRadialGradient(r, r * 0.8, r * 0.1, r, r, r);
  g.addColorStop(0, '#fbf6ea');
  g.addColorStop(1, '#e9dfc8');
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(r, r, r, 0, Math.PI * 2); ctx.fill();
  ctx.translate(r, r);
  // minute ticks
  for (let i = 0; i < 60; i++) {
    const a = (i / 60) * Math.PI * 2;
    const big = i % 5 === 0;
    ctx.strokeStyle = '#3a3128';
    ctx.lineWidth = big ? 6 : 2;
    ctx.beginPath();
    ctx.moveTo(Math.sin(a) * r * 0.9, -Math.cos(a) * r * 0.9);
    ctx.lineTo(Math.sin(a) * r * (big ? 0.8 : 0.86), -Math.cos(a) * r * (big ? 0.8 : 0.86));
    ctx.stroke();
  }
  // numbers 1–12
  const numerals = ['12', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
  ctx.fillStyle = '#2b241d';
  ctx.font = 'bold 52px Georgia, serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  numerals.forEach((n, i) => {
    const a = (i / 12) * Math.PI * 2;
    ctx.fillText(n, Math.sin(a) * r * 0.66, -Math.cos(a) * r * 0.66);
  });
  ctx.font = 'italic 24px Georgia, serif';
  ctx.fillStyle = '#7a5a2e';
  ctx.fillText('David Raj Paul', 0, r * 0.3);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
};

const BRASS = { color: '#b8913f', metalness: 0.85, roughness: 0.3 };

export const WallClock = ({ timeOfDay, localTimeOfDay, nightRef, radius = 0.55, ...props }) => {
  const face = useMemo(makeFaceTexture, []);
  const faceMat = useMemo(() => new THREE.MeshStandardMaterial({ map: face, emissiveMap: face, emissive: new THREE.Color('#fff2d6'), emissiveIntensity: 0, roughness: 0.6 }), [face]);
  useEffect(() => () => { face.dispose(); faceMat.dispose(); }, [face, faceMat]);
  const hour = useRef(), minute = useRef(), second = useRef();
  // ?midnight starts the clock ten seconds before midnight
  const midnightPreview = useMemo(() => {
    if (!isMidnightPreview()) return 0;
    const now = new Date(), target = new Date(now);
    target.setHours(23, 59, 50, 0);
    return target - now;
  }, []);
  const offset = useRef(midnightPreview || clockOffsetFor(timeOfDay, localTimeOfDay));
  const firstChange = useRef(true);
  useEffect(() => {
    if (firstChange.current) { firstChange.current = false; return; } // keep the preview offset at first
    offset.current = clockOffsetFor(timeOfDay, localTimeOfDay);
  }, [timeOfDay, localTimeOfDay]);
  const lastHour = useRef(null);

  useFrame(() => {
    const d = new Date(Date.now() + offset.current);
    const s = d.getSeconds() + d.getMilliseconds() / 1000;
    const m = d.getMinutes() + s / 60;
    const h = (d.getHours() % 12) + m / 60;
    const hour = d.getHours();
    if (lastHour.current === 23 && hour === 0) {
      eggState.chimeAt = performance.now();
      playBells(12);
      announceEgg('midnight', 'Midnight! The clock rang twelve times and the picture lights answered.', '🕛');
    }
    lastHour.current = hour;
    if (second.current) second.current.rotation.z = -(s / 60) * Math.PI * 2;
    if (minute.current) minute.current.rotation.z = -(m / 60) * Math.PI * 2;
    if (hour.current) hour.current.rotation.z = -(h / 12) * Math.PI * 2;
    faceMat.emissiveIntensity = (nightRef ? nightRef.current : 0) * 0.35; // softly lit at night
  });

  const hand = (len, width, color, z) => (
    <mesh position={[0, len / 2 - len * 0.15, z]}>
      <boxGeometry args={[width, len, 0.012]} />
      <meshStandardMaterial color={color} metalness={0.4} roughness={0.5} />
    </mesh>
  );

  return (
    <group {...props}>
      {/* brass rim and back */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, -0.03]}>
        <cylinderGeometry args={[radius + 0.03, radius + 0.05, 0.08, 64]} />
        <meshStandardMaterial {...BRASS} />
      </mesh>
      <mesh position={[0, 0, 0.012]}>
        <torusGeometry args={[radius + 0.025, 0.035, 12, 64]} />
        <meshStandardMaterial {...BRASS} />
      </mesh>
      <mesh position={[0, 0, 0.012]} material={faceMat}>
        <circleGeometry args={[radius, 64]} />
      </mesh>
      <group ref={hour}>{hand(radius * 0.55, 0.035, '#1f1a15', 0.03)}</group>
      <group ref={minute}>{hand(radius * 0.8, 0.022, '#1f1a15', 0.045)}</group>
      <group ref={second}>{hand(radius * 0.88, 0.008, '#a3241c', 0.06)}</group>
      <mesh position={[0, 0, 0.07]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 0.02, 20]} />
        <meshStandardMaterial {...BRASS} />
      </mesh>
    </group>
  );
};

export default WallClock;
